import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CheckBox from "../components/inputs/CheckBox";
import AccessInfoBadges from "../components/layout/AccessInfoBadges";

function DisclaimerRow({
  checked,
  onChange,
  disabled = false,
  children,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      role="checkbox"
      aria-checked={checked}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      onClick={() => {
        if (!disabled) onChange();
      }}
      onKeyDown={(event) => {
        if (disabled) return;
        if (event.key === " " || event.key === "Enter") {
          event.preventDefault();
          onChange();
        }
      }}
      className={`w-full text-left flex gap-[10px] items-center rounded-[8px] bg-[#F7F7F9] px-[12px] py-[12px] ${
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
      }`}
      style={{ boxShadow: "2px 2px 10px 0 rgba(0, 0, 0, 0.05)" }}
    >
      <span className="pointer-events-none">
        <CheckBox
          checked={checked}
          onChange={() => {}}
          isEnabled={!disabled}
          checkmarkColor="#7C3AED"
          borderColor="border-[#D9D9E3]"
        />
      </span>
      <span className="sm:text-[12px] text-[10px] leading-[1.5] text-[#737585]">
        {children}
      </span>
    </div>
  );
}

function DisclaimerPage() {
  const navigate = useNavigate();

  const [notEuResident, setNotEuResident] = useState(false);
  const [ownInitiative, setOwnInitiative] = useState(false);
  const [accurateInfo, setAccurateInfo] = useState(false);

  useEffect(() => {
    const savedInstanceId = sessionStorage.getItem("obd_instance_id");
    const savedRegion = sessionStorage.getItem("obd_region") || "eur";
    if (savedInstanceId) {
      navigate(`/review/${savedRegion}/${savedInstanceId}`, { replace: true });
    }
  }, [navigate]);

  const canContinue = (notEuResident || ownInitiative) && accurateInfo;

  return (
    <div className="relative h-dvh w-full overflow-hidden">
      <div className="absolute inset-0 w-full h-full">
        <div className="noise w-full h-full" />
      </div>

      <div className="relative h-full w-full flex items-center justify-center px-[16px] lg:px-[80px] py-[20px] lg:py-[40px] overflow-hidden">
        <div className="w-full h-full flex flex-col min-h-0">
          <div className="inline-flex self-start items-center px-[12px] py-[6px] rounded-full border border-[#1418A8] text-[#1418A8] text-[12px] lg:text-[14px] font-normal tracking-[0.04em] mb-[28px] shrink-0">
            TYLT EXCHANGE
          </div>

          <div
            className="flex-1 min-h-0 bg-bg-page-body rounded-[18px] flex flex-col overflow-hidden"
            style={{ boxShadow: "5px 5px 25px 0 rgba(0, 0, 0, 0.10)" }}
          >
            <div className="flex-1 min-h-0 overflow-y-auto px-[12px] sm:px-[16px] pt-[24px] pb-[8px]">
              <p className="text-text-base text-[10px] sm:text-[14px] font-bold mb-[24px]">
                Important Eligibility and Access Declaration
              </p>

              <div className="text-[10px] sm:text-[12px] leading-[1.55] text-text-subtle mb-[24px]">
                <p>
                  Before continuing with the On-Ramp Exchange Service, please
                  carefully review and confirm the following.
                </p>
                <p>
                  This service is not marketed, promoted, solicited or actively
                  offered by Tylt to persons who reside in, are established in,
                  or are accessing the service from within the European Union or
                  European Economic Area (&ldquo;EU/EEA&rdquo;).
                </p>
                <p className="text-text-subtle pt-4 ">
                  By selecting the confirmations below and continuing, you
                  expressly declare and confirm that:
                </p>
              </div>

              <div className="space-y-[24px]">
                <DisclaimerRow
                  checked={notEuResident}
                  disabled={ownInitiative}
                  onChange={() => {
                    if (notEuResident) {
                      setNotEuResident(false);
                      return;
                    }
                    setNotEuResident(true);
                    setOwnInitiative(false);
                  }}
                >
                  This service is not marketed, promoted, solicited or actively
                  offered by Tylt to persons who reside in, are established in,
                  or are accessing the service from within the European Union,
                  European Economic Area (“EU/EEA”), or the United Kingdom
                  (“UK”).
                </DisclaimerRow>

                <DisclaimerRow
                  checked={ownInitiative}
                  disabled={notEuResident}
                  onChange={() => {
                    if (ownInitiative) {
                      setOwnInitiative(false);
                      return;
                    }
                    setOwnInitiative(true);
                    setNotEuResident(false);
                  }}
                >
                  I accessed this service independently and entirely on my own
                  initiative. My decision to access and use this service was not
                  made in response to any advertisement, marketing
                  communication, promotional activity, recommendation, direct
                  communication or other solicitation by Tylt, its affiliates,
                  representatives, partners or any person acting on its behalf.
                </DisclaimerRow>

                <div
                  className="rounded-[8px] bg-[#F7F7F9] px-[12px] py-[12px]"
                  style={{ boxShadow: "2px 2px 10px 0 rgba(0, 0, 0, 0.05)" }}
                >
                  <div
                    role="checkbox"
                    aria-checked={accurateInfo}
                    tabIndex={0}
                    onClick={() => setAccurateInfo((prev) => !prev)}
                    onKeyDown={(event) => {
                      if (event.key === " " || event.key === "Enter") {
                        event.preventDefault();
                        setAccurateInfo((prev) => !prev);
                      }
                    }}
                    className="w-full text-left flex gap-[10px] items-start cursor-pointer"
                  >
                    <span className="mt-[1px] pointer-events-none">
                      <CheckBox
                        checked={accurateInfo}
                        onChange={() => {}}
                        checkmarkColor="#7C3AED"
                        borderColor="border-[#D9D9E3]"
                      />
                    </span>
                    <span className="sm:text-[12px] text-[10px] leading-[1.5] text-[#737585]">
                      I have provided accurate and complete information. I
                      understand that Tylt is relying on these declarations in
                      determining whether I am eligible to access this payment
                      method. I agree to notify Tylt if my country of residence,
                      location or other relevant circumstances change before the
                      transaction is completed.
                    </span>
                  </div>
                  <p className="mt-[10px] pl-[28px] sm:text-[12px] text-[10px] leading-[1.5] text-[#737585]">
                    I understand that Tylt may request additional information or
                    documentation to verify my eligibility. If Tylt determines
                    that I am residing in, established in, or accessing the
                    service from the EU/EEA or the United Kingdom, or that any
                    declaration made above is inaccurate, Tylt may decline,
                    suspend or cancel the transaction and restrict access to
                    this payment method, subject to applicable law.
                  </p>
                </div>
              </div>
            </div>

            <div className="px-[20px] sm:px-[28px] pb-[24px] shrink-0">
              <p className="mb-[14px] text-[11px] sm:text-[12px] leading-[1.55] text-text-subtle">
                By clicking &ldquo;Confirm and Continue,&rdquo; I confirm that I
                have read, understood and accepted all the declarations above
                and that I am eligible to proceed.
              </p>
              <div className="flex justify-end gap-[10px]">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="h-[48px] px-[20px] rounded-full text-[12px] sm:text-[16px] font-semibold text-text-subtle bg-[#EFEFF3]"
                >
                  Go Back
                </button>
                <button
                  type="button"
                  disabled={!canContinue}
                  onClick={() => navigate("/instance")}
                  className="h-[48px] px-[20px] rounded-full text-white text-[12px] sm:text-[16px] font-semibold disabled:opacity-60"
                  style={{
                    background:
                      "linear-gradient(90deg, #0E18A7 0%, #5E0EAF 51%, #AE0DB2 100%)",
                  }}
                >
                  Confirm and Continue
                </button>
              </div>
              <AccessInfoBadges />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DisclaimerPage;
